// src/features/home/HomePage.tsx
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Calendar, Stethoscope, Clock, ArrowRight, Activity,
  CheckCircle, UserCheck, Users, AlertTriangle, Sun, Moon,
  PlusCircle, Search as SearchIcon,
} from 'lucide-react';
import { useAuthStore } from '../auth/auth.store';
import {
  appointmentService, receptionService, doctorDashboardService,
} from '../../api/services';
import { format, parseISO, formatDistanceToNow } from 'date-fns';
import clsx from 'clsx';
import { statusLabel } from '../../utils/statusLabel';

function QuickAction({ icon: Icon, label, desc, color, onClick, badge }: {
  icon: any; label: string; desc: string; color: string; onClick: () => void; badge?: number;
}) {
  return (
    <button
      onClick={onClick}
      className="card p-5 text-left hover:shadow-card-hover transition-all duration-200 group flex items-start gap-4"
    >
      <div className={clsx('w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-105 relative', color)}>
        <Icon className="w-6 h-6" />
        {badge !== undefined && badge > 0 && (
          <span className="absolute -top-1.5 -right-1.5 bg-red-500 text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
            {badge > 9 ? '9+' : badge}
          </span>
        )}
      </div>
      <div className="flex-1">
        <div className="font-semibold text-slate-900 group-hover:text-brand-700 transition-colors">{label}</div>
        <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
      </div>
      <ArrowRight className="w-4 h-4 text-slate-300 group-hover:text-brand-400 transition-colors mt-1 flex-shrink-0" />
    </button>
  );
}

export function HomePage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const role = user?.role;
  const isStaff = role === 'Admin' || role === 'Reception';

  const [availDate, setAvailDate] = useState(format(new Date(), 'yyyy-MM-dd'));
  const [showDoctors, setShowDoctors] = useState(false);

  // Patient data
  const { data: myAppointments = [] } = useQuery({
    queryKey: ['my-appointments'],
    queryFn: appointmentService.getMyAppointments,
    enabled: role === 'Patient',
  });

  // Staff dashboard stats — cached 5 min, refetch every 5 min
  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['dashboard-stats', availDate],
    queryFn: () => receptionService.getDashboardStats(availDate),
    enabled: isStaff,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });

  // Doctor data
  const { data: todayPatients = [] } = useQuery({
    queryKey: ['doctor-today'],
    queryFn: doctorDashboardService.getToday,
    enabled: role === 'Doctor',
  });

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      {/* Hero greeting */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-full bg-brand-600 flex items-center justify-center text-white font-bold">
            {user?.name.charAt(0)}
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-slate-900">
              {greeting}, {user?.name.split(' ')[0]}!
            </h1>
            <p className="text-sm text-slate-500">
              {format(new Date(), 'EEEE, MMMM d yyyy')} · {role}
            </p>
          </div>
        </div>
      </div>

      {/* ═══ ADMIN / RECEPTION DASHBOARD — Loading ═══ */}
      {isStaff && statsLoading && (
        <div className="animate-pulse">
          {/* Stats skeleton */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            {[1,2,3,4,5].map(i => (
              <div key={i} className="card p-4 border border-slate-100">
                <div className="h-3 w-20 bg-slate-200 rounded mb-3" />
                <div className="h-8 w-16 bg-slate-200 rounded" />
              </div>
            ))}
          </div>
          {/* Content skeleton */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <div className="lg:col-span-2 space-y-4">
              <div className="h-4 w-32 bg-slate-200 rounded" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[1,2,3,4].map(i => (
                  <div key={i} className="card p-5 flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-slate-200" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-28 bg-slate-200 rounded" />
                      <div className="h-3 w-40 bg-slate-100 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="card p-5 space-y-3">
              <div className="h-4 w-32 bg-slate-200 rounded" />
              <div className="h-8 w-full bg-slate-100 rounded" />
              {[1,2,3,4,5].map(i => (
                <div key={i} className="flex items-center gap-2.5 p-2">
                  <div className="w-8 h-8 rounded-full bg-slate-200" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3.5 w-24 bg-slate-200 rounded" />
                    <div className="h-2.5 w-16 bg-slate-100 rounded" />
                  </div>
                  <div className="h-5 w-10 bg-slate-100 rounded-full" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ═══ ADMIN / RECEPTION DASHBOARD ═══ */}
      {isStaff && !statsLoading && stats && (
        <>
          {/* Stats strip */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
            {[
              { label: 'Total Patients', value: stats.totalPatients, icon: Users, bg: 'bg-slate-50', text: 'text-slate-700', border: 'border-slate-200' },
              { label: 'Today', value: stats.todayAppointments, icon: Calendar, bg: 'bg-brand-50', text: 'text-brand-700', border: 'border-brand-200' },
              { label: 'Pending', value: stats.pendingCount, icon: Clock, bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200' },
              { label: 'In Queue', value: stats.inQueueCount, icon: Activity, bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-200' },
              { label: 'Completed', value: stats.completedToday, icon: CheckCircle, bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
            ].map((stat) => (
              <div key={stat.label} className={clsx('card p-4 border', stat.border)}>
                <div className={clsx('flex items-center gap-2', stat.text)}>
                  <stat.icon className="w-4 h-4" />
                  <span className="text-xs font-semibold uppercase tracking-wide">{stat.label}</span>
                </div>
                <div className={clsx('text-3xl font-bold mt-2', stat.text)}>{stat.value}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* ── Urgent Pending (left 2 cols) ── */}
            <div className="lg:col-span-2">
              {stats.urgentPending.length > 0 && (
                <div className="card border border-red-200 bg-red-50/50 p-5 mb-6">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle className="w-5 h-5 text-red-500" />
                    <h3 className="font-semibold text-red-800 text-sm">
                      Immediate Action Required — {stats.urgentPending.length} Pending &gt; 3 Hours
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {stats.urgentPending.map((item) => (
                      <div key={item.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-red-100">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-slate-900 text-sm">{item.patientName}</span>
                            <span className="font-mono text-xs text-slate-400">{item.appointmentNumber}</span>
                          </div>
                          <div className="text-xs text-slate-500 mt-0.5">
                            {item.doctorName} · {item.departmentName}
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <div className="text-xs font-bold text-red-600">{item.hoursPending}h pending</div>
                          <div className="text-[10px] text-slate-400" title={format(parseISO(item.createdAt), 'MMM d, h:mm a')}>
                            {formatDistanceToNow(parseISO(item.createdAt), { addSuffix: true })}
                          </div>
                        </div>
                        <button
                          onClick={() => navigate('/reception/pending')}
                          className="btn btn-sm bg-red-600 text-white hover:bg-red-700 flex-shrink-0"
                        >
                          Review
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Quick actions */}
              <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Quick Actions</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <QuickAction
                  icon={PlusCircle}
                  label="Quick Book"
                  desc="Book appointment for walk-in or call-in patient"
                  color="bg-brand-100 text-brand-700"
                  onClick={() => navigate('/reception/quick-book')}
                />
                <QuickAction
                  icon={Clock}
                  label="Pending Approvals"
                  desc={`${stats.pendingCount} appointments awaiting review`}
                  color="bg-amber-100 text-amber-700"
                  onClick={() => navigate('/reception/pending')}
                  badge={stats.urgentPending.length}
                />
                <QuickAction
                  icon={Activity}
                  label="Today's Queue"
                  desc="Manage today's patient flow"
                  color="bg-purple-100 text-purple-700"
                  onClick={() => navigate('/reception/queue')}
                />
                <QuickAction
                  icon={Stethoscope}
                  label="Doctor Directory"
                  desc="View all doctors and schedules"
                  color="bg-teal-100 text-teal-700"
                  onClick={() => navigate('/doctors')}
                />
              </div>
            </div>

            {/* ── Available Doctors (right col) ── */}
            <div>
              <div className="card p-5 sticky top-32">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                    <Stethoscope className="w-4 h-4 text-brand-600" />
                    Available Doctors
                  </h3>
                  <button
                    onClick={() => setShowDoctors(!showDoctors)}
                    className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                  >
                    {showDoctors ? 'Hide' : `Show all (${stats.availableDoctors.length})`}
                  </button>
                </div>

                {/* Date picker */}
                <div className="mb-3">
                  <input
                    type="date"
                    className="input w-full text-xs h-8"
                    value={availDate}
                    onChange={(e) => setAvailDate(e.target.value)}
                    min={format(new Date(), 'yyyy-MM-dd')}
                  />
                  <p className="text-[10px] text-slate-400 mt-1">
                    {availDate === format(new Date(), 'yyyy-MM-dd') ? "Today's availability" : format(parseISO(availDate), 'EEE, MMM d')}
                    {' · '}{stats.availableDoctors.length} doctor{stats.availableDoctors.length !== 1 ? 's' : ''}
                  </p>
                </div>

                {/* Doctor list */}
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {(showDoctors ? stats.availableDoctors : stats.availableDoctors.slice(0, 5)).map((doc) => (
                    <div key={doc.id} className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-slate-50 transition-colors">
                      <div className="w-8 h-8 rounded-full bg-brand-100 text-brand-700 flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {doc.name.charAt(0)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-slate-800 truncate" title={`${doc.title ?? ''} ${doc.name}`.trim()}>
                          {doc.name}
                        </div>
                        <div className="text-[10px] text-slate-400 truncate">{doc.departmentName}</div>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        {doc.sessions.map((s) => (
                          <span key={s} className={clsx(
                            'inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded-full font-medium',
                            s === 'Morning' ? 'bg-amber-50 text-amber-600' : 'bg-indigo-50 text-indigo-600'
                          )}>
                            {s === 'Morning' ? <Sun className="w-2.5 h-2.5" /> : <Moon className="w-2.5 h-2.5" />}
                            {s.slice(0, 3)}
                          </span>
                        ))}
                        {doc.isOnCall && (
                          <span className="text-[10px] px-1.5 py-0.5 bg-orange-50 text-orange-600 rounded-full font-medium">OC</span>
                        )}
                      </div>
                    </div>
                  ))}
                  {stats.availableDoctors.length === 0 && (
                    <p className="text-xs text-slate-400 text-center py-4">No doctors available on this date</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══ DOCTOR DASHBOARD ═══ */}
      {role === 'Doctor' && (
        <div className="card p-5 mb-8 border-brand-200 bg-brand-50">
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-brand-600" />
            <div>
              <div className="font-semibold text-brand-900">
                {todayPatients.length} patient{todayPatients.length !== 1 ? 's' : ''} scheduled today
              </div>
              <div className="text-xs text-brand-600 mt-0.5">
                {todayPatients.filter(p => p.status === 'CHECKED_IN').length} waiting ·{' '}
                {todayPatients.filter(p => p.status === 'IN_CONSULTATION').length} in consultation ·{' '}
                {todayPatients.filter(p => p.status === 'COMPLETED').length} completed
              </div>
            </div>
            <button
              onClick={() => navigate('/doctor/today')}
              className="btn btn-primary btn-sm ml-auto"
            >
              View Queue <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}

      {/* ═══ PATIENT DASHBOARD ═══ */}
      {role === 'Patient' && myAppointments.length > 0 && (
        <div className="card p-5 mb-8 border-brand-200">
          <h3 className="font-semibold text-slate-800 mb-3 text-sm">Upcoming Appointments</h3>
          <div className="space-y-2">
            {myAppointments
              .filter(a => ['PENDING', 'CONFIRMED', 'CHECKED_IN'].includes(a.status))
              .slice(0, 3)
              .map(appt => (
                <div key={appt.id} className="flex items-center gap-3 p-2 rounded-lg bg-slate-50">
                  <Calendar className="w-4 h-4 text-brand-500 flex-shrink-0" />
                  <div className="flex-1 text-sm">
                    <span className="font-medium text-slate-900">{appt.doctorName}</span>
                    <span className="text-slate-400 mx-2">·</span>
                    <span className="text-slate-600">{appt.appointmentDate}</span>
                  </div>
                  <span className={`status-${appt.status}`}>{statusLabel(appt.status)}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Quick actions for non-staff roles */}
      {!isStaff && (
        <div>
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {role === 'Patient' && (
              <>
                <QuickAction icon={Calendar} label="Book an Appointment" desc="Schedule a visit with a specialist" color="bg-brand-100 text-brand-700" onClick={() => navigate('/book')} />
                <QuickAction icon={Stethoscope} label="Find a Doctor" desc="Browse doctors by specialty" color="bg-teal-100 text-teal-700" onClick={() => navigate('/doctors')} />
                <QuickAction icon={Clock} label="My Appointments" desc="View and manage your bookings" color="bg-purple-100 text-purple-700" onClick={() => navigate('/appointments')} />
              </>
            )}
            {role === 'Doctor' && (
              <QuickAction icon={UserCheck} label="Today's Patients" desc={`${todayPatients.length} patients in your queue`} color="bg-brand-100 text-brand-700" onClick={() => navigate('/doctor/today')} />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
